/**
 * Counts a number up when it scrolls into view.
 *
 * Reads the target straight out of the rendered text, so nothing has to be
 * re-entered in the editor: "2M+" counts 0 → 2 and keeps the "M+", "100%" keeps its
 * "%", "1,200" keeps its separator. Anything without a number is left alone.
 *
 * Auto-inits from app.ts on `[data-count-up]`.
 */
const DURATION = 1400;
function parse(text) {
    const match = text.match(/^(\D*?)([\d][\d.,]*)(.*)$/s);
    if (!match) {
        return null;
    }
    const raw = match[2];
    // A comma is a thousands separator here, a dot is a decimal point.
    const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    const value = parseFloat(raw.replace(/,/g, ''));
    if (!isFinite(value)) {
        return null;
    }
    return { prefix: match[1], suffix: match[3], value, decimals, grouped: raw.includes(',') };
}
function format(parsed, current) {
    let out = current.toFixed(parsed.decimals);
    if (parsed.grouped) {
        const [whole, fraction] = out.split('.');
        out = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (fraction ? '.' + fraction : '');
    }
    return parsed.prefix + out + parsed.suffix;
}
export default class CountUp {
    /** Returns null when there is no number to animate, so the caller can skip it. */
    static create(element) {
        var _a;
        const parsed = parse(((_a = element.textContent) !== null && _a !== void 0 ? _a : '').trim());
        return parsed ? new CountUp(element, parsed) : null;
    }
    constructor(element, parsed) {
        this.done = false;
        this.element = element;
        this.parsed = parsed;
        // Honour the OS setting: leave the final value in place and never animate.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        // Reserve the final width before zeroing the text, so the row cannot reflow
        // as the digits change.
        this.element.style.minWidth = `${this.element.getBoundingClientRect().width}px`;
        this.element.textContent = format(parsed, 0);
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting || this.done) {
                    return;
                }
                this.done = true;
                observer.disconnect();
                this.run();
            });
        }, { threshold: 0.4 });
        observer.observe(this.element);
    }
    run() {
        const start = performance.now();
        const step = (now) => {
            const progress = Math.min(1, (now - start) / DURATION);
            // easeOutCubic — fast off the mark, settles onto the final figure.
            const eased = 1 - Math.pow(1 - progress, 3);
            this.element.textContent = format(this.parsed, this.parsed.value * eased);
            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
            else {
                // Land on the exact original string rather than a rounded rebuild.
                this.element.textContent = format(this.parsed, this.parsed.value);
                this.element.style.removeProperty('min-width');
            }
        };
        window.requestAnimationFrame(step);
    }
}
